/**
 * What a multi-pack contains: the single items inside it and how many of each. Normally derived
 * from the Danican number (SBL-101+101); this is the hand override for packs numbered otherwise.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SkuMasterService, { SkuItem } from "services/SkuMasterService";

interface PackComponentsDialogProps {
  /** The pack being edited; null when closed */
  pack: SkuItem | null;
  items: SkuItem[];
  service: SkuMasterService;
  onClose: () => void;
  onSaved: () => void;
}

type ComponentRow = { item: SkuItem | null; qty: number };

const itemLabel = (item: SkuItem) => `${item.manufacturerSku || item.shipbobSku || item.shipbobInventoryId} — ${item.name || ""}`;

export default function PackComponentsDialog({ pack, items, service, onClose, onSaved }: PackComponentsDialogProps): JSX.Element {
  const [rows, setRows] = useState<ComponentRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const itemsById = useMemo(() => new Map(items.map(item => [String(item.shipbobInventoryId), item])), [items]);
  // A component is a single item: not this pack, not another pack
  const componentOptions = useMemo(
    () => items.filter(item => item.shipbobInventoryId !== pack?.shipbobInventoryId && item.packComponents.length === 0),
    [items, pack],
  );

  useEffect(() => {
    if (!pack) return;
    setErrorMessage(null);
    const current = pack.packComponents.map(component => ({ item: itemsById.get(component.inventoryId) ?? null, qty: component.qty }));
    setRows(current.length > 0 ? current : [{ item: null, qty: 2 }]);
  }, [pack, itemsById]);

  const updateRow = (index: number, patch: Partial<ComponentRow>) => setRows(previous => previous.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const filledRows = rows.filter(row => row.item);
  const hasDuplicate = new Set(filledRows.map(row => row.item!.shipbobInventoryId)).size !== filledRows.length;
  const isDerived = pack?.packComponents.some(component => component.source === "danican_number");
  // A pack holds single items only, and this one is already inside a pack (the server refuses it too)
  const isInsideAPack = (pack?.soldInPacks.length ?? 0) > 0;

  const save = async (components: Array<{ inventoryId: string; qty: number }>) => {
    if (!pack) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await service.setPackComponents(pack.shipbobInventoryId, components);
      onSaved();
      onClose();
    } catch (error: any) {
      setErrorMessage(error?.message || String(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={pack !== null} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>What does this pack contain?</DialogTitle>
      <DialogContent>
        {pack && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{pack.name || pack.shipbobSku}</strong> ({pack.manufacturerSku || "no Danican #"})
          </Typography>
        )}
        {isInsideAPack && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This item is inside {pack!.soldInPacks.map(entry => itemsById.get(entry.inventoryId)?.manufacturerSku || entry.inventoryId).join(", ")}, so it
            can't be a pack itself.
          </Alert>
        )}
        {isDerived && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Worked out from the Danican number. Saving here fixes it by hand; later syncs won't change it.
          </Alert>
        )}
        <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 1 }}>
          The Inventory page adds a pack's sales and stock to its single item's, so the product can be seen as a whole.
        </Typography>
        {rows.map((row, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Autocomplete
              size="small"
              sx={{ flex: 1 }}
              options={componentOptions}
              getOptionLabel={itemLabel}
              value={row.item}
              onChange={(_, item) => updateRow(index, { item })}
              renderInput={(params) => <TextField {...params} label="Single item inside the pack" />}
            />
            <TextField
              size="small"
              type="number"
              label="Units"
              value={row.qty}
              inputProps={{ min: 1, step: 1, style: { width: 60 } }}
              onChange={(event) => updateRow(index, { qty: Math.max(1, Math.floor(Number(event.target.value) || 1)) })}
            />
            <IconButton size="small" onClick={() => setRows(previous => previous.filter((_, i) => i !== index))} disabled={rows.length === 1}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" onClick={() => setRows(previous => [...previous, { item: null, qty: 1 }])} sx={{ textTransform: "none" }}>
          + Another item (a mixed pack, e.g. Queen + King)
        </Button>
        {hasDuplicate && <Alert severity="warning" sx={{ mt: 1 }}>The same item is listed twice — put the total in one row.</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}
      </DialogContent>
      <DialogActions>
        {pack && pack.packComponents.length > 0 && (
          <Button color="inherit" disabled={isSaving} onClick={() => save([])} sx={{ mr: "auto" }}>
            Not a pack
          </Button>
        )}
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={isSaving || isInsideAPack || filledRows.length === 0 || hasDuplicate}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          onClick={() => save(filledRows.map(row => ({ inventoryId: String(row.item!.shipbobInventoryId), qty: row.qty })))}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
